"use client"

/**
 * Web Widget Settings Panel
 *
 * Appearance, branding, and token management for web_widget channels.
 */

import { useCallback, useEffect, useRef, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { CollapsibleInfo } from "@/components/ui/collapsible-info"
import { KeyRound, Loader2, Save, Sparkles } from "lucide-react"
import { toast } from "sonner"
import { useAuth } from "@/hooks/auth/use-auth"
import type { MessageAgentChannel } from "@/hooks/agents/messaging/use-agents"
import type { WebWidgetConfig } from "@/lib/agents/messaging/types"
import { WIDGET_BUBBLE_OFFSET, WIDGET_BUBBLE_SIZE, WIDGET_DEFAULT_CONFIG } from "@/lib/agents/messaging/channels/web-widget/constants"
import { WidgetPreview, WIDGET_PREVIEW_WIDTH } from "./widget-preview"
import { WidgetAppearanceFields } from "./widget-appearance-fields"
import { AvatarUploadControl } from "./avatar-upload-control"
import { RegenerateTokenButton } from "./regenerate-token-button"

import { WidgetContactSettings } from './widget-contact-settings'
import type { ContactProvider } from '@/lib/agents/messaging/channels/web-widget/contact-channels/model'
import { useTranslations } from "next-intl"



interface WebWidgetSettingsProps {
  onDirtyChange?: (dirty: boolean) => void
  onConnect?: (provider: ContactProvider) => void
  channel: MessageAgentChannel
  onRefresh: () => void
  canManage: boolean
}

export function WebWidgetSettings({ channel, onRefresh, canManage, onConnect, onDirtyChange }: WebWidgetSettingsProps) {
  const tUi = useTranslations("Messaging.AgentsMessagingSettingsWebWidgetSettings")

  const { fetchWithAuth } = useAuth()
  const [tab, setTab] = useState<"appearance" | "channels">("appearance")
  const [channelsVisited, setChannelsVisited] = useState(false)
  const channelConfig = (channel.config ?? {}) as Partial<WebWidgetConfig>

  const [config, setConfig] = useState<WebWidgetConfig>({
    ...WIDGET_DEFAULT_CONFIG,
    ...channelConfig,
  })
  const [savedConfig, setSavedConfig] = useState(config)
  const [channelsDirty, setChannelsDirty] = useState(false)
  useEffect(() => {
    onDirtyChange?.(JSON.stringify(config) !== JSON.stringify(savedConfig) || channelsDirty)
  }, [config, savedConfig, channelsDirty, onDirtyChange])
  const [isSaving, setIsSaving] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [canCustomizeBranding, setCanCustomizeBranding] = useState(false)
  const promptInfoRef = useRef<HTMLDivElement>(null)

  // Scroll the expanded info block into view inside the right column's own scroll container.
  const handlePromptInfoOpen = useCallback((open: boolean) => {
    if (!open) return
    requestAnimationFrame(() => {
      promptInfoRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
    })
  }, [])

  useEffect(() => {
    fetchWithAuth(`/api/agents/messaging/channels/web-widget/config?channelId=${channel.id}`)
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setCanCustomizeBranding(data.canCustomizeBranding) })
      .catch(() => {})
  }, [channel.id, fetchWithAuth])

  const handleSave = async () => {
    setError(null)
    setIsSaving(true)
    try {
      const response = await fetchWithAuth("/api/agents/messaging/channels/web-widget/config", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ channelId: channel.id, config }),
      })

      if (!response.ok) {
        const data = await response.json()
        setError(data.error || "Failed to save settings")
        return
      }

      setSavedConfig(config)
      toast.success(tUi("widgetSettingsSaved"), {
        description: tUi("changesMayTakeUpTo15MinutesTo"),
      })
      onRefresh()
    } catch {
      setError(tUi("failedToSaveSettings"))
    } finally {
      setIsSaving(false)
    }
  }

  const handleRegenerateToken = async () => {
    setError(null)
    setIsRegenerating(true)
    try {
      const response = await fetchWithAuth("/api/agents/messaging/channels/web-widget/regenerate-token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ channelId: channel.id }),
      })

      if (!response.ok) {
        const data = await response.json()
        setError(data.error || "Failed to regenerate token")
        return
      }

      toast.success(tUi("tokenRegeneratedUpdateEmbedCodeOnYourWebsite"))
      onRefresh()
    } catch {
      setError(tUi("failedToRegenerateToken"))
    } finally {
      setIsRegenerating(false)
    }
  }

  const avatarControl = (
    <AvatarUploadControl
      avatarUrl={config.avatarUrl}
      channelId={channel.id}
      uploadPath="/api/agents/messaging/channels/web-widget/avatar"
      canManage={canManage}
      onChange={(url) => {
        setConfig({ ...config, avatarUrl: url })
        if (url) onRefresh()
      }}
      onError={setError}
    />
  )

  return (
    <div className="space-y-4">
      <div role="tablist" aria-label={tUi("webChatSettings")} className="flex gap-2 border-b pb-2">
        <Button id="widget-appearance-tab" role="tab" aria-controls="widget-appearance-panel" aria-selected={tab === 'appearance'} variant={tab === 'appearance' ? 'default' : 'ghost'} onClick={() => setTab('appearance')}><span key={tUi("appearance")}>{tUi("appearance")}</span></Button>
        <Button id="widget-channels-tab" role="tab" aria-controls="widget-channels-panel" aria-selected={tab === 'channels'} variant={tab === 'channels' ? 'default' : 'ghost'} onClick={() => { setChannelsVisited(true); setTab('channels') }}><span key={tUi("channels")}>{tUi("channels")}</span></Button>
      </div>
      {channelsVisited && <div hidden={tab !== 'channels'} id="widget-channels-panel" role="tabpanel" aria-labelledby="widget-channels-tab"><WidgetContactSettings onDirtyChange={setChannelsDirty} channelId={channel.id} appearance={config} canManage={canManage} onRefresh={onRefresh} onConnect={onConnect} /></div>}
      <div id="widget-appearance-panel" role="tabpanel" aria-labelledby="widget-appearance-tab" className="flex gap-6" style={{ display: tab === 'appearance' ? undefined : 'none' }}>
      {/* Left column: settings */}
      <div className="flex-1 min-w-0 space-y-6">
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}

      <WidgetAppearanceFields
        config={config}
        onChange={setConfig}
        canManage={canManage}
        avatarControl={avatarControl}
        canCustomizeBranding={canCustomizeBranding}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base"><span key={tUi("windowPlacement")}>{tUi("windowPlacement")}</span></CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label><span key={tUi("position")}>{tUi("position")}</span></Label>
          <div className="flex gap-2">
            {(["bottom-left", "bottom-right"] as const).map((pos) => (
              <Button
                key={pos}
                variant={config.position === pos ? "default" : "outline"}
                size="sm"
                onClick={() => setConfig({ ...config, position: pos })}
                disabled={!canManage}
              >
                <span key={pos === "bottom-right" ? tUi("bottomRight") : tUi("bottomLeft")}>{pos === "bottom-right" ? tUi("bottomRight") : tUi("bottomLeft")}</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* --- Chat Button --- */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base"><span key={tUi("chatButton")}>{tUi("chatButton")}</span></CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label><span key={tUi("style")}>{tUi("style")}</span></Label>
              <div className="flex gap-2">
                {(["filled", "outline"] as const).map((style) => (
                  <Button
                    key={style}
                    variant={config.bubbleStyle === style ? "default" : "outline"}
                    size="sm"
                    onClick={() => setConfig({ ...config, bubbleStyle: style })}
                    disabled={!canManage}
                  >
                    <span key={style === "filled" ? tUi("filled") : tUi("outline")}>{style === "filled" ? tUi("filled") : tUi("outline")}</span>
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label><span key={tUi("entranceAnimation")}>{tUi("entranceAnimation")}</span></Label>
              <div className="flex flex-wrap gap-2">
                {(["none", "bounce", "pulse", "wiggle"] as const).map((anim) => (
                  <Button
                    key={anim}
                    variant={(config.bubbleAnimation ?? "bounce") === anim ? "default" : "outline"}
                    size="sm"
                    onClick={() => setConfig({ ...config, bubbleAnimation: anim })}
                    disabled={!canManage}
                  >
                    <span key={anim === "none" ? tUi("none") : anim.charAt(0).toUpperCase() + anim.slice(1)}>{anim === "none" ? tUi("none") : anim.charAt(0).toUpperCase() + anim.slice(1)}</span>
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {config.bubbleAnimation !== "none" && (
            <div className="space-y-2">
              <Label><span><span key={tUi("animationDelay")}>{tUi("animationDelay")}</span></span> <span className="text-gray-400 font-normal">{config.bubbleAnimationDelay ?? 3}s</span></Label>
              <Slider
                value={[config.bubbleAnimationDelay ?? 3]}
                onValueChange={([v]) => setConfig({ ...config, bubbleAnimationDelay: v })}
                min={0}
                max={10}
                step={1}
                disabled={!canManage}
              />
            </div>
          )}

          <PxSlider
            label={tUi("buttonSize")}
            value={config.bubbleSize ?? WIDGET_BUBBLE_SIZE.default}
            range={WIDGET_BUBBLE_SIZE}
            step={4}
            onChange={(bubbleSize) => setConfig({ ...config, bubbleSize })}
            disabled={!canManage}
          />

          <div className="grid grid-cols-2 gap-4">
            <PxSlider
              label={tUi("offsetFromSide")}
              value={config.bubbleOffsetX ?? WIDGET_BUBBLE_OFFSET.default}
              range={WIDGET_BUBBLE_OFFSET}
              step={5}
              onChange={(bubbleOffsetX) => setConfig({ ...config, bubbleOffsetX })}
              disabled={!canManage}
            />
            <PxSlider
              label={tUi("offsetFromBottom")}
              value={config.bubbleOffsetY ?? WIDGET_BUBBLE_OFFSET.default}
              range={WIDGET_BUBBLE_OFFSET}
              step={5}
              onChange={(bubbleOffsetY) => setConfig({ ...config, bubbleOffsetY })}
              disabled={!canManage}
            />
          </div>
        </CardContent>
      </Card>

      {/* --- Website --- */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base"><span key={tUi("website")}>{tUi("website")}</span></CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label htmlFor="widget-site-url"><span key={tUi("websiteURL")}>{tUi("websiteURL")}</span></Label>
          <Input
            id="widget-site-url"
            type="url"
            placeholder={"https://example.com"}
            value={config.siteUrl ?? ""}
            onChange={(e) => setConfig({ ...config, siteUrl: e.target.value })}
            disabled={!canManage}
            className="text-sm"
          />
          <p className="text-xs text-gray-500">
            <span key={tUi("thePageWhereThisWidgetIsInstalledSaved")}>{tUi("thePageWhereThisWidgetIsInstalledSaved")}</span></p>
        </CardContent>
      </Card>

      {/* --- Publish --- */}
      {canManage && (
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={isSaving} className="bg-blue-600 hover:bg-blue-700">
            {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            <span><span key={tUi("publishAppearance")}>{tUi("publishAppearance")}</span></span>
          </Button>
        </div>
      )}

      {canManage ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <KeyRound className="h-4 w-4" />
              <span><span key={tUi("embedCodeAccess")}>{tUi("embedCodeAccess")}</span></span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              <span key={tUi("theInstallationCodeIsShownDirectlyOnThis")}>{tUi("theInstallationCodeIsShownDirectlyOnThis")}</span></p>
            <RegenerateTokenButton
              onConfirm={handleRegenerateToken}
              loading={isRegenerating}
            />
          </CardContent>
        </Card>
      ) : null}
      </div>

      <div className="hidden lg:flex lg:flex-col lg:items-center sticky top-0 self-start pt-2 pb-4 gap-4 max-h-screen overflow-y-auto">
        <WidgetPreview config={config} />

        <div
          ref={promptInfoRef}
          className="w-full"
          style={{ maxWidth: WIDGET_PREVIEW_WIDTH }}
        >
          <CollapsibleInfo
            title={tUi("aIOutputFormat")}
            icon={<Sparkles className="w-4 h-4 text-indigo-500" />}
            titleClassName="text-sm"
            onOpenChange={handlePromptInfoOpen}
          >
            <div className="space-y-3 text-sm text-gray-600">
              <p>
                <span><span key={tUi("theAgentSSystemPromptIs")}>{tUi("theAgentSSystemPromptIs")}</span></span> <strong><span key={tUi("automaticallyEnriched")}>{tUi("automaticallyEnriched")}</span></strong>{" "}
                <span><span key={tUi("withMarkdownFormattingRulesWheneverAConversationRuns")}>{tUi("withMarkdownFormattingRulesWheneverAConversationRuns")}</span></span>
              </p>

              <div>
                <p className="font-medium text-gray-700 mb-1"><span key={tUi("supported")}>{tUi("supported")}</span></p>
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  <li>
                    <span><span key={tUi("links")}>{tUi("links")}</span></span> <code className="bg-gray-100 px-1 rounded">[text](url)</code>{" "}
                    <span><span key={tUi("only")}>{tUi("only")}</span></span> <code className="bg-gray-100 px-1 rounded">http</code><span>,</span>{" "}
                    <code className="bg-gray-100 px-1 rounded">https</code><span>,</span>{" "}
                    <code className="bg-gray-100 px-1 rounded">mailto</code>
                  </li>
                  <li>
                    <strong><span key={tUi("bold")}>{tUi("bold")}</span></strong><span>,</span> <em><span key={tUi("italic")}>{tUi("italic")}</span></em><span>,</span> <s><span key={tUi("strikethrough")}>{tUi("strikethrough")}</span></s>
                  </li>
                  <li><span key={tUi("bulletAndNumberedLists")}>{tUi("bulletAndNumberedLists")}</span></li>
                  <li>
                    <span><span key={tUi("inline")}>{tUi("inline")}</span></span> <code className="bg-gray-100 px-1 rounded">code</code>{" "}
                    <span><span key={tUi("andFencedCodeBlocks")}>{tUi("andFencedCodeBlocks")}</span></span>
                  </li>
                  <li><span key={tUi("blockquotes")}>{tUi("blockquotes")}</span></li>
                </ul>
              </div>

              <div>
                <p className="font-medium text-gray-700 mb-1"><span key={tUi("strippedOnRender")}>{tUi("strippedOnRender")}</span></p>
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  <li>
                    <span><span key={tUi("headings")}>{tUi("headings")}</span></span><code className="bg-gray-100 px-1 rounded"># H1</code> <span>…</span>{" "}
                    <code className="bg-gray-100 px-1 rounded">###### H6</code><span><span key={tUi("use")}>{tUi("use")}</span></span>{" "}
                    <strong><span key={tUi("bold")}>{tUi("bold")}</span></strong> <span><span key={tUi("forEmphasisInstead")}>{tUi("forEmphasisInstead")}</span></span>
                  </li>
                  <li>
                    <span><span key={tUi("taskLists")}>{tUi("taskLists")}</span></span>
                    <code className="bg-gray-100 px-1 rounded">- [ ] item</code><span><span key={tUi("checkboxElementsAreNotAllowed")}>{tUi("checkboxElementsAreNotAllowed")}</span></span>
                  </li>
                  <li><span key={tUi("tablesImagesRawHTML")}>{tUi("tablesImagesRawHTML")}</span></li>
                  <li>
                    <span><span key={tUi("disallowedURLSchemesEG")}>{tUi("disallowedURLSchemesEG")}</span></span>{" "}
                    <code className="bg-gray-100 px-1 rounded">javascript:</code><span>,</span>{" "}
                    <code className="bg-gray-100 px-1 rounded">data:</code><span>)</span>
                  </li>
                </ul>
              </div>

              <p className="text-xs text-gray-500 italic">
                <span key={tUi("otherChannelsInstagramFacebookTelegramStayPlainText")}>{tUi("otherChannelsInstagramFacebookTelegramStayPlainText")}</span></p>
            </div>
          </CollapsibleInfo>
        </div>
      </div>
      </div>
    </div>
  )
}

function PxSlider({ label, value, range, step, onChange, disabled }: {
  label: string
  value: number
  range: { min: number; max: number; default: number }
  step: number
  onChange: (value: number) => void
  disabled: boolean
}) {
  const tUi = useTranslations("Messaging.AgentsMessagingSettingsWebWidgetSettings")

  const suffix = value === range.default ? " (default)" : ""
  return (
    <div className="space-y-2">
      <Label><span>{label}</span> <span className="text-gray-400 font-normal">{<span key={tUi("value0PxValue1", { value0: String(value), value1: String(suffix) })}>{tUi("value0PxValue1", { value0: String(value), value1: String(suffix) })}</span>}</span></Label>
      <Slider
        value={[value]}
        onValueChange={([next]) => onChange(next)}
        min={range.min}
        max={range.max}
        step={step}
        disabled={disabled}
      />
    </div>
  )
}
